import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import blogData from '../data/blogs';

const BlogSidebar = ({ maxBlogs = 5, layout = "grid" }) => {
  const [recentBlogs, setRecentBlogs] = useState([]);
  const location = useLocation();

  useEffect(() => {
    // Create a seed from the current pathname for consistent randomization per page
    const seed = location.pathname.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    // Shuffle array using seeded random
    const shuffleArray = (array, seed) => {
      const shuffled = [...array];
      let currentIndex = shuffled.length;
      let temporaryValue, randomIndex;
      
      // Use seed to generate pseudo-random numbers
      let random = seed;
      const seededRandom = () => {
        random = (random * 9301 + 49297) % 233280;
        return random / 233280;
      };
      
      while (currentIndex !== 0) {
        randomIndex = Math.floor(seededRandom() * currentIndex);
        currentIndex -= 1;
        temporaryValue = shuffled[currentIndex];
        shuffled[currentIndex] = shuffled[randomIndex];
        shuffled[randomIndex] = temporaryValue;
      }
      
      return shuffled;
    };
    
    // Get random blogs based on page path
    const shuffledBlogs = shuffleArray(blogData, seed);
    setRecentBlogs(shuffledBlogs.slice(0, maxBlogs));
  }, [maxBlogs, location.pathname]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (recentBlogs.length === 0) return null;

  return (
    <div className={`grid grid-cols-1 ${layout === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
      {recentBlogs.map((blog) => (
        <Link
          key={blog.id}
          to={`/blog/${blog.slug}`}
          className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
        >
          {/* Blog Image */}
          <div className="relative h-48 overflow-hidden bg-gray-200">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
            <div className="absolute top-4 left-4">
              <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                {blog.category}
              </span>
            </div>
          </div>

          {/* Blog Content */}
          <div className="p-6">
            <div className="flex items-center text-sm text-gray-500 mb-3">
              <span>{formatDate(blog.date)}</span>
              <span className="mx-2">•</span>
              <span>{blog.readTime}</span>
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
              {blog.title}
            </h3>
            
            <p className="text-gray-600 mb-4 line-clamp-2 text-sm">
              {blog.excerpt}
            </p>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-semibold text-sm">
                    {blog.author.charAt(0)}
                  </span>
                </div>
                <span className="text-sm text-gray-600">{blog.author}</span>
              </div>
              
              <span className="text-primary-600 font-medium group-hover:translate-x-1 transition-transform inline-flex items-center text-sm">
                Read →
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default BlogSidebar;

